/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import MsDyn365, { ILinkData } from '@msdyn365-commerce/core';
import { IHeaderViewProps, Logo } from '@msdyn365-commerce-modules/header';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IHeaderConfig, IHeaderProps } from '../definition-extensions/header.ext.props.autogenerated';
import { isOnHomePage } from '../../../shared-utils/common-utilities';

/**
 * GridSizes.
 */
export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface IHeaderViewState {
    searchActive: boolean;
}

/**
 *
 * HeaderView component.
 * @extends {React.PureComponent<IHeaderViewProps>}
 */
export class HeaderView extends React.PureComponent<IHeaderViewProps & IHeaderProps<{}>, IHeaderViewState> {
    private readonly menuNode: React.RefObject<HTMLDivElement>;

    public constructor(props: IHeaderViewProps & IHeaderProps<{}>) {
        super(props);
        this.state = {
            searchActive: false
        };
        this.menuNode = React.createRef();
    }

    public componentDidMount(): void {
        if (this.props.config.useStickyHeader) {
            // Set header update methods to trigger on scroll
            if (MsDyn365.isBrowser) {
                window.addEventListener('scroll', () => {
                    this._configureStickyHeader();
                });
            }
            this._configureStickyHeader();
        }

        if (MsDyn365.isBrowser) {
            const searchContainer = document.querySelector('.ms-search');

            searchContainer?.addEventListener('click', (e) => {
                if (!this.state.searchActive) {
                    // First click will "activate" the search bar, therefore we need to stop the event propagation
                    this.setState({ searchActive: true });
                    e.stopPropagation();
                }
            });

            searchContainer?.addEventListener('keyup', (e: Event) => {
                const event = e as KeyboardEvent;
                if (event.key === 'Escape') {
                    this.setState({ searchActive: false });
                }
            });

            // Detect click outside of search container
            document.addEventListener('click', (e) => {
                if (!searchContainer?.contains(e.target as Node)) {
                    this.setState({ searchActive: false });
                }
            });
        }
    }

    public render(): JSX.Element | null {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        const {
            HeaderTag,
            HeaderContainer,
            HeaderTopBarContainer,
            Divider,
            MobileMenuContainer,
            MobileMenuBodyContainer,
            MobileMenuLinksContainer
        } = this.props;

        // Change aria label resource string that is used as 'title' and display it via CSS
        const navIconResources = (this.props.navIcon as React.ReactElement).props.resources;
        const navIconFixed = this.props.menuBar.length > 0 ? React.cloneElement(this.props.navIcon as React.ReactElement, {
            resources: {
                ...navIconResources,
                mobileHamburgerAriaLabel: this.props.resources.headerProductsLabel || navIconResources.mobileHamburgerAriaLabel
            }
        }) : null;
        // Get products direct link from menu bar if it exists
        const productsDirectLink = (this.props.menuBar?.[0] as React.ReactElement | undefined)?.props.config.productsDirectLink as ILinkData | undefined;

        return (
            <Module {...HeaderTag}>
                <Node {...HeaderContainer}>
                    <Node {...HeaderTopBarContainer}>
                        <div className='ms-header__topbar--left'>
                            {this.props.logo}
                            {this._renderMobileLogo()}
                            {this.props.config.logoText && (
                                <span className='ms-header__logo-text'>
                                    {this.props.config.logoText}
                                </span>
                            )}
                        </div>

                        <div className='ms-header__topbar--center'>
                            <div className='desktop-navicon-container' ref={this.menuNode}>
                                {this._renderReactFragment(this.props.menuBar)}
                            </div>
                            <div className='mobile-navicon-container'>
                                {productsDirectLink ? (
                                    <a className='ms-header__nav-icon' href={productsDirectLink.destinationUrl}>
                                        {this.props.resources.headerProductsLabel}
                                    </a>
                                ) : (
                                    navIconFixed
                                )}
                            </div>
                            {this.props.search && ArrayExtensions.hasElements(this.props.search) && (
                                <Node className={classnames(`${HeaderTopBarContainer.className}__search-container`, { active: this.state.searchActive })}>
                                    {this._renderReactFragment(this.props.search)}
                                </Node>
                            )}
                            <a
                                className='ms-header__hamburger-menu'
                                href={productsDirectLink?.destinationUrl}
                                title={typeof window !== 'undefined' && window.toggleSortAndFilterModal
                                    ? this.props.resources.headerSortAndFilterAriaLabel
                                    : this.props.resources.headerProductsLabel
                                }
                                onClick={(e) => {
                                    if (window.toggleSortAndFilterModal && window.toggleSortAndFilterModal()) {
                                        e.preventDefault();
                                    }
                                }}
                            />
                        </div>

                        <div className='ms-header__topbar--right'>
                            {this.props.preferredStore}
                            {this.props.currentDistributor}
                            {this._renderAccountBlock(this.props)}
                            <Node {...Divider} />
                            {this.props.cartIcon}
                            {this._renderReactFragment(this.props.siteOptions)}
                        </div>
                    </Node>
                    <Node {...MobileMenuContainer}>
                        <Node {...MobileMenuBodyContainer}>
                            {this.props.MobileMenuHeader}
                            {this._renderReactFragment(this.props.menuBar)}
                            <Node {...MobileMenuLinksContainer}>
                                {this.props.accountLinks ? this.props.accountLinks.map(link => link) : false}
                                {this.props.siteOptions}
                                {this.props.wishListIconMobile}
                                {this.props.signInLink}
                                {this.props.signOutLink}
                            </Node>
                        </Node>
                    </Node>
                </Node>
            </Module>
        );
    }

    /**
     * Function to update header.
     */
    private readonly _configureStickyHeader = (): void => {
        // Get heights of cookie and promotion banners
        const defaultValue = 0;
        const headerAlertsContainer: HTMLElement | null = document.querySelector('.ms-promo-banner');
        const bannerHeights = headerAlertsContainer ? headerAlertsContainer.offsetHeight : defaultValue;

        // Triggers opacity change of header
        const headerElement = document.querySelector('header .default-container');
        if (headerElement) {
            if (MsDyn365.isBrowser && document.documentElement.scrollTop > defaultValue) {
                headerElement.classList.add('lock-opaque');
                headerElement.classList.add('fixed');
            } else {
                headerElement.classList.remove('lock-opaque');
                headerElement.classList.remove('fixed');
            }
        }

        // Update sticky header position and opacity
        const stickyHeader: HTMLElement | null = document.querySelector('.desktop-navicon-container');
        const headerLogo: HTMLElement | null = document.querySelector('.ms-header__logo');

        if (stickyHeader && headerLogo) {
            // Fix center sticky header
            const navStickyPos = headerLogo.offsetHeight + bannerHeights;
            if (MsDyn365.isBrowser && document.documentElement.scrollTop > navStickyPos) {
                stickyHeader.classList.add('fixed');
            } else {
                stickyHeader.classList.remove('fixed');
            }
        }
    };

    private _renderMobileLogo(): JSX.Element | null {
        const { config, context, typeName } = this.props;
        return (
            <Logo
                {...{
                    link: config.logoLink,
                    image: config.mobileLogoImage ?? {},
                    className: 'ms-header__mobile-logo',
                    gridSettings: context.request.gridSettings,
                    requestContext: context.request,
                    typeName
                }}
            />
        );
    }

    /**
     * Function to render account block.
     * @param props - IHeaderViewProps.
     * @returns Returns JSX.Element | null.
     */
    private readonly _renderAccountBlock = (props: IHeaderViewProps): JSX.Element | null => {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        const {
            AccountInfoDropdownParentContainer,
            AccountInfoDropdownPopoverConentContainer,
            accountInfoDropdownButton,
            signInLink
        } = props;
        if (AccountInfoDropdownParentContainer) {
            const accountClassName = classnames(AccountInfoDropdownParentContainer.className, 'account-desktop');
            if (AccountInfoDropdownPopoverConentContainer) {
                return (
                    <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                        {accountInfoDropdownButton}
                        <Node {...AccountInfoDropdownPopoverConentContainer}>{this._renderAccountDropdownLinks(props)}</Node>
                    </Node>
                );
            } else if (signInLink) {
                let extSignInLink = signInLink;
                const config = this.props.config as IHeaderConfig;

                // If on the landing page and a sign-in redirect URL is configured, append it as a 'ru' query parameter to the sign-in link
                if (isOnHomePage(props.context) && config.landingPageSignInRedirectUrl?.destinationUrl) {
                    try {
                        let extSignInHref = (signInLink as React.ReactElement).props.href as string;

                        const url = new URL(extSignInHref);
                        url.searchParams.set('ru', props.context.request.url.requestUrl.origin + config.landingPageSignInRedirectUrl.destinationUrl);
                        extSignInHref = url.toString();

                        extSignInLink = React.cloneElement(signInLink as React.ReactElement, {
                            href: extSignInHref
                        });
                    } catch (e) {
                        console.error('Error processing sign-in redirect URL', e);
                    }
                }

                return (
                    <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                        {extSignInLink}
                    </Node>
                );
            }
        }
        return null;
    };

    private readonly _renderAccountDropdownLinks = (props: IHeaderViewProps): JSX.Element | null => {
        const { isOBORequest, switchCustomerLink, accountLinks, signOutLink } = props;
        if (isOBORequest) {
            return (
                <>
                    {switchCustomerLink}
                    {signOutLink}
                    {accountLinks ? <Node className='ms-profile-button-splitter' /> : false}
                    {accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false}
                </>
            );
        } else {
            return (
                <>
                    {accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false}
                    {signOutLink}
                </>
            );
        }
    };

    /**
     * Function to render react fragment.
     * @param items - React nodes.
     * @returns Returns JSX.Element | null.
     */
    private readonly _renderReactFragment = (items: React.ReactNode[]): JSX.Element | null => {
        return (
            <>
                {ArrayExtensions.hasElements(items)
                    ? items.map((slot: React.ReactNode) => {
                          return <>{slot}</>;
                      })
                    : null}
            </>
        );
    };
}

export default HeaderView;
